import { useQuery } from "@tanstack/react-query";
import type { ControlcenterFetchInterface } from "@dealer-center/ddc-pages-controlcenter-interface";

const queryKeyBase = "ddc-pages-controlcenter";

/**
 * The signed-in operator's ControlCenter context, optionally resolved **as** another user.
 *
 * ## ⚠️ `asAccount` and `asUser` are part of the cache key — do not remove them
 *
 * They were missing until 2026-09-22, while still being interpolated into the request URL. The
 * response therefore described whichever impersonation target happened to be fetched **first**, for
 * every later pair:
 *
 * 1. the key was `[base, resource, extraURLParams]`, and both callers pass `resource: ""` /
 *    `extraURLParams: ""`, so **every** `/cc/*` route shared one cache entry;
 * 2. `staleTime` is 5 minutes, so a changed pair did not even refetch in the background;
 * 3. neither caller remounts on a route **param** change — `StrictlyInternalGuard` and
 *    `PagesControlcenterApp` are the same elements at the same positions for
 *    `/cc/as/:asAccount/:asUser/*` — so no new observer was created to trigger one either.
 *
 * Net effect: an in-app switch from `/cc/as/A/u1/...` to `/cc/as/B/u2/...` kept serving A's
 * `ccUserInfo`, so every page below it — its account id, its permissions, its operator identity —
 * described the wrong account until a full document load. Silent: a stale-but-valid payload renders
 * perfectly.
 *
 * **The deliberate cache sharing between the guard and the app is preserved.** That is load-bearing
 * — both components' comments describe the guard *pre-warming* this query so the app can render
 * `<Outlet />` on its first commit — and it still works, because the two derive the same values for
 * a given URL: the guard splits them out of `pathname`, the app reads `useParams()`, and on the
 * non-`as` `/cc` route both yield `undefined`, which the defaults here turn into `""` for both.
 */
export function useControlcenterAPIInterface({
  resource = "",
  asAccount = "",
  asUser = "",
  extraURLParams = ""
}: ControlcenterFetchInterface) {
  return useQuery({
    queryKey: [queryKeyBase, resource, asAccount, asUser, extraURLParams],
    queryFn: async () => {
      const rstr = resource ? `/${resource}` : "";
      const res = await fetch(
        `/landing/nxlambda/controlcenter${rstr}?asAccount=${asAccount}&asUser=${asUser}${extraURLParams}`
      );
      if (res.ok) {
        return await res.json();
      } else {
        throw new Error(res.statusText);
      }
    },
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}
